import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown, BookOpen, MapPin } from 'lucide-react';
import { useLang } from '../i18n';

const Line = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: '115%' }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -10 });
  else el.scrollIntoView({ behavior: 'smooth' });
};

const HeroSection = () => {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 140]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-16"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 55% at 50% 0%, rgba(15,35,42,0.9) 0%, rgba(7,14,18,0) 70%)',
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 40% at 50% 110%, rgba(212,175,55,0.10) 0%, rgba(7,14,18,0) 70%)',
        }}
      />

      <motion.div
        style={{ y, opacity }}
        className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full"
      >
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="font-mono-alt text-[10px] sm:text-xs tracking-[0.35em] uppercase text-[#e6c687] mb-8"
        >
          {t('hero.overline')}
        </motion.p>

        <h1 className="font-display font-medium text-[#f3e5ab] leading-[0.95] text-5xl sm:text-7xl lg:text-8xl tracking-tight">
          <Line delay={0.25}>Trinity</Line>
          <Line delay={0.4}>
            <span className="italic font-light text-[#d4af37]">Masterclass</span>
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 max-w-xl text-base md:text-lg text-[#a3b1b6] leading-relaxed"
        >
          {t('hero.subtitle')}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-browse-btn"
            onClick={() => scrollTo('#sfoglia')}
            className="group inline-flex items-center gap-3 rounded-full bg-[#d4af37] px-7 py-3.5 font-mono-alt text-[11px] tracking-[0.2em] uppercase text-[#070e12] font-medium transition-all duration-300 hover:bg-[#f3e5ab] hover:shadow-[0_0_40px_-8px_rgba(212,175,55,0.6)]"
          >
            <BookOpen size={15} strokeWidth={1.8} />
            {t('hero.cta1')}
          </button>
          <button
            data-testid="hero-cta-venues-btn"
            onClick={() => scrollTo('#sedi')}
            className="inline-flex items-center gap-3 rounded-full border border-[#d4af37]/40 px-7 py-3.5 font-mono-alt text-[11px] tracking-[0.2em] uppercase text-[#e6c687] transition-colors duration-300 hover:bg-[#d4af37]/10"
          >
            <MapPin size={15} strokeWidth={1.8} />
            {t('hero.cta2')}
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-hint"
        onClick={() => scrollTo('#sfoglia')}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#a3b1b6] hover:text-[#f3e5ab] transition-colors duration-300"
        style={{ animation: 'float-slow 3.2s ease-in-out infinite' }}
        aria-label={t('hero.scroll')}
      >
        <ChevronDown size={26} strokeWidth={1.2} />
      </motion.button>
    </section>
  );
};

export default HeroSection;
